import { useState, type FormEvent } from 'react';
import { Icon } from '~/components/ui';
import { useLocale, useT } from '~/i18n';
import { formatLongDate } from '~/lib/date';
import {
  PHONE_COUNTRIES,
  composeE164,
  formatE164,
  lastCallWasShort,
  splitE164,
  type CallLine,
  type CandidateEnglishAssessment,
} from '~/lib/englishAssessment';
import { EnglishClientError, saveWhatsappPhone } from '../api/englishClient';

interface Props {
  assessment: CandidateEnglishAssessment;
  phone: string | null;
  /** El número vino precargado del cuestionario y la persona todavía no dijo que es el suyo. */
  confirmed: boolean;
  line: CallLine;
  onPhoneSaved: (phone: string) => void;
}

/**
 * «<Empresa> quiere conocer tu nivel de inglés»: la tarjeta con la que el
 * candidato hace la llamada. Explica qué es (IA, ~10 min, se graba, quién ve el
 * resultado), le deja confirmar o cambiar su WhatsApp (es lo que encuentra su
 * evaluación cuando llama) y le da el botón grande para llamar.
 */
export default function EnglishCallCard({ assessment, phone, confirmed, line, onPhoneSaved }: Props) {
  const t = useT('english').candidate;
  useLocale();
  const company = assessment.agency.name || assessment.posting.company;
  const deadline = formatLongDate(assessment.expires_at, '');
  const waitingCall = assessment.status === 'requested' || assessment.status === 'notified';

  return (
    <div className="card rise" data-testid="english-call-card">
      <div className="card-body space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-brand mt-0.5">
            <Icon name="mic" size={20} />
          </span>
          <div className="min-w-0">
            <h2 className="card-title text-lg">{company ? t.cardTitle(company) : t.cardTitleFallback}</h2>
            {assessment.posting.title && <p className="text-sm hush">{t.forPosting(assessment.posting.title)}</p>}
          </div>
        </div>

        {assessment.status === 'in_call' && <p className="text-sm font-medium">{t.inCall}</p>}
        {assessment.status === 'scoring' && <p className="text-sm font-medium">{t.scoring}</p>}

        {waitingCall && (
          <>
            {lastCallWasShort(assessment) && (
              <div className="alert text-sm">
                <span>{t.shortCall(assessment.calls_left)}</span>
              </div>
            )}
            <p className="text-sm">{t.howItWorks}</p>
            <p className="text-sm">
              {t.free} {deadline && t.deadline(deadline)}
            </p>

            <PhoneField phone={phone} confirmed={confirmed} onSaved={onPhoneSaved} />

            {phone && confirmed ? (
              <a
                href={line.call_link}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-lg w-full gap-2"
                style={{ background: '#25D366', color: '#0b1f17', borderColor: '#25D366', fontWeight: 700 }}
                data-testid="english-call-button"
              >
                <Icon name="bubble" size={20} />
                {t.call}
              </a>
            ) : (
              <button type="button" className="btn btn-lg w-full" disabled>
                {t.callFirst}
              </button>
            )}
            <p className="text-xs hush">{t.callFallback(line.display)}</p>
            <p className="text-xs hush">{t.tips}</p>
          </>
        )}
      </div>
    </div>
  );
}

function PhoneField({
  phone,
  confirmed,
  onSaved,
}: {
  phone: string | null;
  confirmed: boolean;
  onSaved: (phone: string) => void;
}) {
  const t = useT('english').candidate;
  const [editing, setEditing] = useState(!phone);
  const initial = splitE164(phone);
  const [dial, setDial] = useState(initial.dial);
  const [local, setLocal] = useState(initial.local);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const save = async (e164: string) => {
    setBusy(true);
    setMessage(null);
    try {
      const saved = await saveWhatsappPhone(e164);
      onSaved(saved);
      setEditing(false);
      setMessage({ kind: 'ok', text: t.phoneSaved });
    } catch (err) {
      const invalid = err instanceof EnglishClientError && err.code === 'invalid_phone';
      setMessage({ kind: 'error', text: invalid ? t.phoneInvalid : t.phoneFailed });
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const e164 = composeE164(dial, local);
    if (!e164) {
      setMessage({ kind: 'error', text: t.phoneInvalid });
      return;
    }
    await save(e164);
  };

  if (!editing && phone) {
    return (
      <div className="flex items-center justify-between gap-2 flex-wrap p-3 rounded-2xl" style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}>
        <div className="min-w-0">
          <span className="brand-label block">{t.phoneLabel}</span>
          <p className="font-semibold mt-1" data-testid="english-phone">{formatE164(phone)}</p>
          <p className="text-xs hush">{confirmed ? t.phoneHelp : t.phoneConfirmQuestion}</p>
          {message && <p className={`text-xs mt-1 ${message.kind === 'ok' ? 'text-data' : 'text-error'}`}>{message.text}</p>}
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
            {t.phoneEdit}
          </button>
          {!confirmed && (
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void save(phone)}>
              {busy && <span className="loading loading-spinner loading-xs" />}
              {t.phoneConfirm}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2 p-3 rounded-2xl" style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}>
      <span className="brand-label block">{t.phoneLabel}</span>
      {!phone && <p className="text-xs">{t.phoneMissing}</p>}
      <div className="flex gap-2 flex-wrap">
        <label className="sr-only" htmlFor="english-phone-country">{t.phoneCountry}</label>
        <select
          id="english-phone-country"
          className="select select-bordered select-sm"
          value={dial}
          onChange={(e) => setDial(e.target.value)}
          aria-label={t.phoneCountry}
        >
          {PHONE_COUNTRIES.map((country) => (
            <option key={`${country.iso}-${country.dial}`} value={country.dial}>
              {country.name} +{country.dial}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor="english-phone-number">{t.phoneNumber}</label>
        <input
          id="english-phone-number"
          className="input input-bordered input-sm flex-1 min-w-[160px]"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={t.phonePlaceholder}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          aria-label={t.phoneNumber}
        />
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
          {busy && <span className="loading loading-spinner loading-xs" />}
          {t.phoneSave}
        </button>
      </div>
      <p className="text-xs hush">{t.phoneHelp}</p>
      {message?.kind === 'error' && <p className="text-xs text-error">{message.text}</p>}
    </form>
  );
}
